// Client API du module « Facture » (/api/factures), brique 5B.
//
// Une facture naît en BROUILLON (sans numéro), se modifie/supprime librement, puis est ÉMISE
// (numéro FAC_AAAA-NNN attribué, document figé et INALTÉRABLE). Les transitions de statut
// passent par des routes dédiées (emettre / statut), pas par le PATCH de brouillon.

import { api } from './api'
import type { FactureStatut, FactureTransition } from '@shared/schemas/facture'
import type { ClientType } from '@shared/schemas/client'
import type { ModeReglement } from '@shared/schemas/compta'
import type { DevisDetail } from './devis'
import type { DepotDetail } from './depots'

export type { FactureStatut, FactureTransition }
export {
  FACTURE_STATUTS,
  LIBELLES_STATUT_FACTURE,
  calculerTotaux,
  UNITES_LIGNE,
  LIBELLES_UNITE_LIGNE,
  ABREV_UNITE_LIGNE,
  abrevUniteLigne,
} from '@shared/schemas/facture'
export type { UniteLigne } from '@shared/schemas/facture'

export interface LigneFacture {
  id: string
  articleId: string | null
  designation: string
  quantite: number
  prixUnitaire: number
  unite: string | null
  ordre: number | null
}

export interface FactureListItem {
  id: string
  numero: string | null
  clientNom: string | null
  statut: FactureStatut
  dateFacture: string
  dateEcheance: string | null
  tvaPct: number
  totalHt: number
  // Encaissement (27/10/2026) : renseignés au passage à « payée » ; null avant cette date.
  dateEncaissement: string | null
  modeReglement: ModeReglement | null
  createdAt: string
}

// Coordonnées du client jointes pour l'en-tête du PDF (null si pas de client / supprimé).
export interface FactureClientDetail {
  type: ClientType
  adresse: string | null
  codePostal: string | null
  ville: string | null
  pays: string
  email: string | null
  telephone: string | null
  numeroTva: string | null
  siret: string | null
}

export interface FactureDetail extends FactureListItem {
  clientId: string | null
  remise: number
  fraisPort: number
  mentionTva: string | null
  emiseLe: string | null
  notes: string | null
  updatedAt: string
  client: FactureClientDetail | null
  lignes: LigneFacture[]
}

export interface LigneFacturePayload {
  articleId?: string | null
  designation: string
  quantite: number
  prixUnitaire: number
  unite?: string | null
  ordre?: number | null
}

// Création / mise à jour d'un BROUILLON (jamais de `statut` ici : émission via route dédiée).
export interface FacturePayload {
  clientId?: string | null
  dateFacture: string
  dateEcheance?: string | null
  remise: number
  fraisPort: number
  tvaPct?: number | null
  notes?: string | null
  lignes: LigneFacturePayload[]
}

export async function fetchFactures(): Promise<FactureListItem[]> {
  const { factures } = await api<{ factures: FactureListItem[] }>('/api/factures')
  return factures
}

export async function fetchUneFacture(id: string): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}`)
  return facture
}

export async function creerFacture(payload: FacturePayload): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>('/api/factures', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return facture
}

export async function majFacture(id: string, payload: Partial<FacturePayload>): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return facture
}

export async function supprimerFacture(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/factures/${id}`, { method: 'DELETE' })
}

// Émet la facture (brouillon -> émise) : numéro attribué, mention TVA figée, sortie de stock 'vente'.
export async function emettreFacture(id: string): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}/emettre`, {
    method: 'POST',
  })
  return facture
}

// Change le statut d'une facture émise : 'payee' ou 'annulee'. Pour 'payee', la date
// d'encaissement et le mode de règlement alimentent le livre des recettes (27/10/2026).
export async function changerStatutFacture(
  id: string,
  statut: FactureTransition,
  encaissement: { dateEncaissement?: string; modeReglement?: ModeReglement | null } = {},
): Promise<FactureDetail> {
  const { facture } = await api<{ facture: FactureDetail }>(`/api/factures/${id}/statut`, {
    method: 'PATCH',
    body: JSON.stringify({ statut, ...encaissement }),
  })
  return facture
}

// Télécharge le PDF Factur-X (PDF/A-3b + XML CII embarqué) d'une facture ÉMISE. On n'utilise PAS
// le helper `api` (qui attend du JSON) : la réponse est un binaire. Le cookie de session part
// automatiquement (credentials: 'include'). En cas d'échec, on relaie le message d'erreur serveur.
export async function telechargerFacturX(id: string, nomFichier: string): Promise<void> {
  const reponse = await fetch(`/api/factures/${id}/facturx`, { credentials: 'include' })
  if (!reponse.ok) {
    let message = 'Échec de la génération du Factur-X.'
    try {
      const corps = (await reponse.json()) as { error?: string }
      if (corps?.error) message = corps.error
    } catch {
      /* réponse non-JSON : on garde le message générique */
    }
    throw new Error(message)
  }
  const blob = await reponse.blob()
  const url = URL.createObjectURL(blob)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nomFichier
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  URL.revokeObjectURL(url)
}

// Actions disponibles sur une facture selon son statut (source unique pour les boutons de la liste).
// Reflète exactement les règles du service : seul un brouillon se modifie/supprime/émet ; une facture
// émise se paie ou s'annule ; une facture payée s'annule ; une facture annulée ne fait plus que
// s'imprimer. L'impression est possible dès qu'un numéro existe (statut ≠ brouillon).
export interface ActionsFacture {
  peutModifier: boolean
  peutSupprimer: boolean
  peutEmettre: boolean
  peutPayer: boolean
  peutAnnuler: boolean
  peutImprimer: boolean
}

export function actionsPourStatut(statut: FactureStatut): ActionsFacture {
  return {
    peutModifier: statut === 'brouillon',
    peutSupprimer: statut === 'brouillon',
    peutEmettre: statut === 'brouillon',
    peutPayer: statut === 'emise',
    peutAnnuler: statut === 'emise' || statut === 'payee',
    peutImprimer: statut !== 'brouillon',
  }
}

// Construit le payload d'un BROUILLON de facture à partir d'un devis (décision a : « convertir
// un devis en facture »). Recopie client + lignes + remise + frais de port + notes ; le devis
// reste inchangé. La date de facture est fournie par l'appelant (le jour de la conversion) —
// fonction PURE, donc pas d'horloge à l'intérieur (testable). On ne fixe pas `tvaPct` : le
// serveur applique le défaut du régime de la cliente (0 en franchise, taux standard au réel).
export function payloadFactureDepuisDevis(devis: DevisDetail, dateFacture: string): FacturePayload {
  return {
    clientId: devis.clientId,
    dateFacture,
    remise: devis.remise,
    fraisPort: devis.fraisPort,
    notes: devis.notes,
    lignes: devis.lignes.map((l, i) => ({
      articleId: l.articleId,
      designation: l.designation,
      quantite: l.quantite,
      prixUnitaire: l.prixUnitaire,
      unite: l.unite,
      ordre: l.ordre ?? i,
    })),
  }
}

// Construit le payload d'un BROUILLON de facture à partir d'un dépôt-vente RÉGULARISÉ
// (brique D4). Le document facture le DÉPOSITAIRE (la boutique) du montant net qui revient à la
// savonnière : `reversement` = total vendu − commission (calculé côté serveur, exposé sur le
// dépôt).
//
// DÉTAIL DES PRODUITS VENDUS : une ligne LIBRE par produit réellement vendu (quantité vendue > 0),
// au prix unitaire du dépôt, et la commission dépositaire portée en REMISE GLOBALE (= `tauxCommission`).
// Le schéma facture interdit les prix négatifs et les quantités ≤ 0 : impossible d'écrire une ligne
// « Commission −X € », d'où la réfaction via remise. Comme `calculerDepot` et `calculerTotaux` partagent
// les MÊMES formules d'arrondi (Math.round(n*100)/100), le total facture = `reversement` au centime :
//   sousTotal      = Σ(quantiteVendue × prixUnitaire) = totalVendu
//   remiseMontant  = sousTotal × tauxCommission%      = commissionMontant
//   totalHt        = sousTotal − remiseMontant        = reversement
//
// Lignes LIBRES (articleId null) : le stock a déjà été sorti à l'ACTIVATION du dépôt (motif 'depot',
// brique D3), donc AUCUN mouvement de stock à l'émission de la facture.
//
// REPLI : si aucune vente (aucune ligne à quantité vendue > 0), on retombe sur une ligne unique globale
// au montant du reversement (évite une facture sans ligne, rejetée par le schéma).
//
// Fonction PURE (la date est fournie par l'appelant, testable). On ne fixe pas `tvaPct` : le serveur
// applique le régime du compte (0 en franchise 293 B).
export function payloadFactureDepuisDepot(depot: DepotDetail, dateFacture: string): FacturePayload {
  const ref = depot.numero ? ` ${depot.numero}` : ''
  const lignesVendues = depot.lignes.filter((l) => l.quantiteVendue > 0)

  // Repli : aucune vente -> ligne unique globale au montant net (remise 0).
  if (lignesVendues.length === 0) {
    return {
      clientId: depot.clientId,
      dateFacture,
      remise: 0,
      fraisPort: 0,
      notes: `Reversement dépôt-vente${ref}`,
      lignes: [
        {
          articleId: null, // ligne libre -> aucune sortie de stock à l'émission (déjà sortie en D3)
          designation: `Reversement dépôt-vente${ref}`,
          quantite: 1,
          prixUnitaire: depot.reversement,
          unite: null,
          ordre: 0,
        },
      ],
    }
  }

  return {
    clientId: depot.clientId,
    dateFacture,
    remise: depot.tauxCommission, // commission dépositaire portée en remise globale (%)
    fraisPort: 0,
    notes: `Reversement dépôt-vente${ref}`,
    lignes: lignesVendues.map((l, i) => ({
      articleId: null, // ligne libre -> aucune sortie de stock à l'émission (déjà sortie en D3)
      designation: l.designation,
      quantite: l.quantiteVendue,
      prixUnitaire: l.prixUnitaire,
      unite: l.unite,
      ordre: i,
    })),
  }
}
